import { useState } from "react";
import type { EventsSectionContent, EventItem } from "@site-factory/config";
import { cn } from "../utils";
import { SectionTitle, SectionSubtitle } from "../typography/Typography";
import { Card, CardContent } from "../components/Card";
import { Button } from "../components/Button";
import { Icon } from "../icons";
import { Modal } from "../components/Modal";
import { BandedSection, type BandedSectionVariant } from "../components/BandedSection";
import { Skeleton, SkeletonText } from "../components/Skeleton";

export interface EventsSectionProps extends EventsSectionContent {
  className?: string;
  bandVariant?: BandedSectionVariant;
  loading?: boolean;
  calendarMode?: "events" | "availability";
  showUpcomingList?: boolean;
  bookingHref?: string;
  bookingLabel?: string;
  enableCalendarToggle?: boolean;
  calendarViewLabels?: {
    eventsLabel?: string;
    availabilityLabel?: string;
    helperText?: string;
  };
}

export function EventsSection({
  title,
  subtitle,
  events,
  calendarLink,
  variant = "list",
  className,
  bandVariant = "plain",
  loading = false,
  calendarMode = "events",
  showUpcomingList = true,
  bookingHref,
  bookingLabel = "Request this date",
  enableCalendarToggle = false,
  calendarViewLabels,
}: EventsSectionProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedEventIndex, setSelectedEventIndex] = useState(0);
  const [activeMode, setActiveMode] = useState<"events" | "availability">(calendarMode);
  const parseLocalDate = (value: string) => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-").map(Number);
      return new Date(year, month - 1, day);
    }
    return new Date(value);
  };

  const formatDate = (value: string | Date) => {
    try {
      const date = value instanceof Date ? value : parseLocalDate(value);
      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(date);
    } catch {
      return value instanceof Date ? value.toISOString() : value;
    }
  };

  // Calendar helper functions
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1).getDay();
  };

  const isSameDay = (date1: Date, date2: Date) => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const getEventsForDate = (date: Date): EventItem[] => {
    return events.filter((event) => {
      try {
        const eventDate = parseLocalDate(event.date);
        return isSameDay(eventDate, date);
      } catch {
        return false;
      }
    });
  };

  const navigateMonth = (direction: "prev" | "next") => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      if (direction === "prev") {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
    setSelectedDate(null);
    setSelectedEventIndex(0);
  };

  const goToToday = () => {
    const now = new Date();
    setCurrentDate(now);
    setSelectedDate(now);
    setSelectedEventIndex(0);
  };

  if (loading) {
    return (
      <BandedSection variant={bandVariant} className="py-16 md:py-24" contentClassName={className}>
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="text-center space-y-2">
            <Skeleton className="h-8 w-64 mx-auto" />
            <Skeleton className="h-4 w-80 mx-auto" />
          </div>
          <div className="grid gap-4">
            {Array.from({ length: 3 }).map((_, idx) => (
              <Card key={idx}>
                <CardContent className="space-y-3">
                  <Skeleton className="h-4 w-32" />
                  <SkeletonText lines={3} />
                  <div className="flex gap-2">
                    <Skeleton className="h-8 w-20" />
                    <Skeleton className="h-8 w-16" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </BandedSection>
    );
  }

  const renderCalendarVariant = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const today = new Date();
    const businessStart = 8 * 60; // 8:00
    const businessEnd = 22 * 60; // 22:00
    const now = new Date();

    const parseTimeToMinutes = (value?: string) => {
      if (!value) return null;
      const trimmed = value.trim();
      // Try HH:MM
      const hhmm = trimmed.match(/^(\d{1,2}):(\d{2})$/);
      if (hhmm) {
        const h = parseInt(hhmm[1], 10);
        const m = parseInt(hhmm[2], 10);
        return h * 60 + m;
      }
      // Try hh:mm AM/PM
      const ampm = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
      if (ampm) {
        let h = parseInt(ampm[1], 10);
        const m = parseInt(ampm[2], 10);
        const isPM = ampm[3].toUpperCase() === "PM";
        if (h === 12) h = 0;
        const mins = h * 60 + m + (isPM ? 12 * 60 : 0);
        return mins;
      }
      return null;
    };

    type AvailabilityStatus = "open" | "limited" | "full" | "past";
    const availabilityForDay = (date: Date): { status: AvailabilityStatus; freeWindows: Array<{ start: number; end: number }> } => {
      const dayEvents = getEventsForDate(date);
      const intervals: Array<[number, number]> = [];
      const isPastDay = date.getTime() < new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      if (isPastDay) {
        return { status: "past", freeWindows: [] as Array<{ start: number; end: number }> };
      }
      dayEvents.forEach((e) => {
        const start = parseTimeToMinutes((e as any).start) || parseTimeToMinutes(e.time?.split("-")[0]?.trim());
        const end = parseTimeToMinutes((e as any).end) || parseTimeToMinutes(e.time?.split("-")[1]?.trim());
        if (start !== null && end !== null && end > start) {
          intervals.push([start, end]);
        }
      });

      if (intervals.length === 0) {
        return { status: "open", freeWindows: [{ start: businessStart, end: businessEnd }] as Array<{ start: number; end: number }> };
      }

      // Merge intervals
      intervals.sort((a, b) => a[0] - b[0]);
      const merged: Array<[number, number]> = [];
      for (const [s, e] of intervals) {
        if (!merged.length || s > merged[merged.length - 1][1]) {
          merged.push([s, e]);
        } else {
          merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], e);
        }
      }

      // Compute free windows within business hours
      const free: Array<{ start: number; end: number }> = [];
      let cursor = businessStart;
      for (const [s, e] of merged) {
        if (cursor < s && s < businessEnd) {
          free.push({ start: cursor, end: Math.min(s, businessEnd) });
        }
        cursor = Math.max(cursor, e);
      }
      if (cursor < businessEnd) {
        free.push({ start: cursor, end: businessEnd });
      }

      const freeMinutes = free.reduce((sum, f) => sum + (f.end - f.start), 0);
      const status = freeMinutes <= 0 ? "full" : freeMinutes < 180 ? "limited" : "open";

      return { status, freeWindows: free };
    };

    const monthName = new Intl.DateTimeFormat("en-US", {
      month: "long",
      year: "numeric",
    }).format(currentDate);

    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    // Generate calendar grid
    const calendarDays: (number | null)[] = [];
    for (let i = 0; i < firstDay; i++) {
      calendarDays.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      calendarDays.push(day);
    }

    const selectedDayEvents = selectedDate ? getEventsForDate(selectedDate) : [];

    // Get upcoming events for at-a-glance view (exclude blocked events)
    const upcomingEvents = showUpcomingList
      ? events
          .filter((event) => {
            try {
              const eventDate = parseLocalDate(event.date);
              return eventDate >= today && !event.blocked;
            } catch {
              return false;
            }
          })
          .sort((a, b) => parseLocalDate(a.date).getTime() - parseLocalDate(b.date).getTime())
          .slice(0, 5)
      : [];

    const availabilityLabel = (status: "open" | "limited" | "full" | "past") => {
      if (status === "open") return "Likely open";
      if (status === "limited") return "Limited";
      if (status === "full") return "Busy";
      return "Past";
    };

    const buildBookingHref = (date: Date) => {
      if (!bookingHref) return null;
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      const joiner = bookingHref.includes("?") ? "&" : "?";
      return `${bookingHref}${joiner}date=${encodeURIComponent(dateStr)}`;
    };

    const eventsLabel = calendarViewLabels?.eventsLabel ?? "Events";
    const availabilityLabelText = calendarViewLabels?.availabilityLabel ?? "Host availability";

    return (
      <>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Calendar */}
          <div
            className={cn(
              "rounded-3xl border border-slate-200/70 bg-white/90 p-6 shadow-sm-2",
              showUpcomingList ? "lg:col-span-2" : "lg:col-span-3"
            )}
          >
            {/* Calendar Header */}
            <div className="flex flex-col gap-4 mb-6 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-1">Month</p>
                <h3 className="text-2xl font-semibold text-slate-950">{monthName}</h3>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {enableCalendarToggle && (
                  <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-1 text-xs font-semibold text-slate-600">
                    <button
                      type="button"
                      onClick={() => setActiveMode("events")}
                      className={cn(
                        "rounded-full px-3 py-1 transition",
                        activeMode === "events" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
                      )}
                    >
                      {eventsLabel}
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveMode("availability")}
                      className={cn(
                        "rounded-full px-3 py-1 transition",
                        activeMode === "availability" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
                      )}
                    >
                      {availabilityLabelText}
                    </button>
                  </div>
                )}
                <Button
                  variant="soft"
                  size="sm"
                  onClick={() => navigateMonth("prev")}
                >
                  ←
                </Button>
                <Button
                  variant="soft"
                  size="sm"
                  onClick={() => navigateMonth("next")}
                >
                  →
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToToday}
                >
                  Today
                </Button>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2">
              {/* Day names */}
              {dayNames.map((day) => (
                <div
                  key={day}
                  className="text-center text-[11px] font-semibold py-1 uppercase tracking-[0.18em] text-slate-400"
                >
                  {day.slice(0, 3)}
                </div>
              ))}

              {/* Calendar days */}
              {calendarDays.map((day, index) => {
                if (day === null) {
                  return <div key={`empty-${index}`} className="aspect-square" />;
                }

                const date = new Date(year, month, day);
                const dayEvents = getEventsForDate(date);
                const hasEvents = dayEvents.length > 0;
                const publicEvents = dayEvents.filter((e) => !e.blocked);
                const blockedEvents = dayEvents.filter((e) => e.blocked);
                const hasPublicEvents = publicEvents.length > 0;
                const hasBlocked = blockedEvents.length > 0;
                const isToday = isSameDay(date, today);
                const isSelected = selectedDate && isSameDay(date, selectedDate);
                const availability = availabilityForDay(date);

                return (
                  <button
                    key={day}
                    onClick={() => {
                      if ((hasPublicEvents || hasBlocked || availability.status !== "past")) {
                        setSelectedDate(date);
                        setSelectedEventIndex(0);
                      }
                    }}
                    disabled={availability.status === "past"}
                    className={cn(
                      "group relative aspect-square rounded-2xl border border-slate-200/70 bg-white p-2 text-center text-sm transition-all hover:border-slate-300 hover:shadow-sm",
                      isToday && "border-primary/50 bg-primary/5 font-semibold ring-1 ring-primary/20",
                      isSelected && "ring-2 ring-primary/40 border-primary/60 bg-primary/5",
                      !hasEvents && !isToday && "text-slate-400",
                      hasPublicEvents && !isToday && "border-primary/30 bg-primary/[0.03] font-semibold",
                      hasBlocked && !hasPublicEvents && "border-amber-300/70 bg-amber-50 text-amber-700",
                      (hasPublicEvents || hasBlocked) && !isToday && "cursor-pointer",
                      !hasPublicEvents && !hasBlocked && "cursor-default"
                    )}
                    title={
                      hasPublicEvents
                        ? `${publicEvents.length} event${publicEvents.length > 1 ? "s" : ""}`
                        : hasBlocked
                        ? `${blockedEvents.length} private booking${blockedEvents.length > 1 ? "s" : ""}`
                        : ""
                    }
                    >
                    <div className={cn("text-sm font-semibold", isToday || isSelected ? "text-primary" : "text-slate-700")}>
                      {day}
                    </div>
                    {activeMode === "events" && (hasPublicEvents || hasBlocked) && (
                      <div className="mt-2 space-y-1">
                        <div className="flex justify-center gap-2">
                          {hasPublicEvents &&
                            publicEvents.slice(0, 3).map((_, i) => (
                              <div
                                key={`pub-${i}`}
                                className="h-2 w-2 rounded-full bg-primary ring-2 ring-white"
                              />
                            ))}
                          {hasBlocked &&
                            blockedEvents.slice(0, 2).map((_, i) => (
                              <div
                                key={`priv-${i}`}
                                className="h-2 w-2 rounded-full bg-amber-500 ring-2 ring-white"
                              />
                            ))}
                        </div>
                        <div className="text-[10px] font-semibold text-slate-500">
                          {hasPublicEvents ? `${publicEvents.length} event${publicEvents.length > 1 ? "s" : ""}` : "Private booking"}
                        </div>
                      </div>
                    )}
                    {activeMode === "availability" && availability.status !== "past" && (
                      <div className="mt-2 flex justify-center">
                        <div
                          className={cn(
                            "h-1.5 w-12 rounded-full",
                            availability.status === "open" && "bg-emerald-400",
                            availability.status === "limited" && "bg-amber-400",
                            availability.status === "full" && "bg-rose-400"
                          )}
                          title={`Availability: ${availabilityLabel(availability.status)}`}
                          aria-label={`Availability: ${availabilityLabel(availability.status)}`}
                        />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {activeMode === "availability" && calendarViewLabels?.helperText && (
              <p className="mt-4 text-xs text-slate-500">{calendarViewLabels.helperText}</p>
            )}

            {/* Legend */}
            <div
              className="flex flex-wrap gap-3 text-xs mt-5"
              style={{ color: "var(--color-text,#0f172a)", opacity: 0.75 }}
            >
              {(activeMode === "availability"
                ? [
                    { label: "Likely open", dotClass: "bg-emerald-500", pill: "bg-emerald-50 border-emerald-200 text-emerald-700" },
                    { label: "Limited", dotClass: "bg-amber-500", pill: "bg-amber-50 border-amber-200 text-amber-700" },
                    { label: "Busy", dotClass: "bg-rose-500", pill: "bg-rose-50 border-rose-200 text-rose-700" },
                  ]
                : [
                    { label: "Today", dotClass: "bg-primary", pill: "bg-primary/10 border-primary text-primary" },
                    { label: "Public Events", dotClass: "bg-primary", pill: "bg-primary/12 border-primary/50 text-primary" },
                    { label: "Booked (Private)", dotClass: "bg-amber-500", pill: "bg-amber-50 border-amber-400/70 text-amber-700" },
                  ]
              ).map((item) => (
                <span
                  key={item.label}
                  className={cn(
                    "inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium shadow-sm",
                    item.pill
                  )}
                >
                  <span className={cn("w-2.5 h-2.5 rounded-full shadow-[0_0_0_4px_rgba(0,0,0,0.05)]", item.dotClass)} />
                  {item.label}
                </span>
              ))}
            </div>
          </div>

          {/* Upcoming Events List */}
          {showUpcomingList && (
            <div className="lg:col-span-1">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-bold">Upcoming Events</h4>
              {calendarLink && (
                <Button asChild variant="ghost" size="sm">
                  <a href={calendarLink.href}>{calendarLink.text}</a>
                </Button>
              )}
            </div>
            {upcomingEvents.length > 0 ? (
              <div className="space-y-3">
                {upcomingEvents.map((event, index) => (
                  <Card
                    key={index}
                    className="p-4 cursor-pointer hover:border-primary transition-all bg-white/80 text-[var(--color-text,#0f172a)] border border-[color:var(--color-text,#0f172a)]/10 shadow-sm hover:shadow-md rounded-2xl"
                    onClick={() => {
                      try {
                        setSelectedDate(parseLocalDate(event.date));
                      } catch (e) {
                        console.error("Invalid date:", e);
                      }
                    }}
                  >
                    <CardContent className="p-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="text-xs font-semibold text-primary mb-1">
                            {formatDate(event.date)}
                          </div>
                          <div className="text-sm font-semibold mb-1" style={{ color: "var(--color-text,#0f172a)" }}>
                            {event.title}
                          </div>
                        </div>
                        <span className="inline-flex items-center justify-center text-[10px] px-2 py-1 rounded-full bg-primary/10 text-primary font-semibold">
                          {event.category || "Event"}
                        </span>
                      </div>
                      {event.time && (
                        <div className="text-xs" style={{ color: "var(--color-text,#0f172a)", opacity: 0.75 }}>
                          <span className="inline-flex items-center gap-1">
                            <Icon name="clock" className="h-3.5 w-3.5" />
                            {event.time}
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-4 bg-white/80 text-[var(--color-text,#0f172a)] border border-[color:var(--color-text,#0f172a)]/10 shadow-sm rounded-2xl">
                <CardContent className="p-0 space-y-3">
                  <div className="text-sm font-semibold">No upcoming events listed yet</div>
                  <p className="text-sm" style={{ color: "var(--color-text,#0f172a)", opacity: 0.72 }}>
                    Follow our Facebook for daily route updates or request a booking to reserve your date.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button asChild variant="primary" size="sm">
                      <a href="/book">Book an event</a>
                    </Button>
                    {calendarLink && (
                      <Button asChild variant="outline" size="sm">
                        <a href={calendarLink.href}>{calendarLink.text}</a>
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          )}
        </div>

        {/* Modal for event details */}
        <Modal
          isOpen={!!selectedDate}
          onClose={() => setSelectedDate(null)}
          title={selectedDate ? formatDate(selectedDate) : ""}
          className="text-neutral-900"
        >
          <div className="space-y-4" style={{ color: "#111827" }}>
            {selectedDate && (() => {
              const dayAvailability = availabilityForDay(selectedDate);
              const color =
                dayAvailability.status === "open"
                  ? "text-emerald-600 bg-emerald-50 border-emerald-200"
                  : dayAvailability.status === "limited"
                  ? "text-amber-600 bg-amber-50 border-amber-200"
                  : dayAvailability.status === "past"
                  ? "text-neutral-600 bg-neutral-100 border-neutral-200"
                  : "text-rose-600 bg-rose-50 border-rose-200";
              return (
                <div className={cn("p-3 rounded-lg border text-sm", color)}>
                  <div className="font-semibold mb-1">Availability</div>
                  {dayAvailability.freeWindows.length === 0 ? (
                    <div>{dayAvailability.status === "past" ? "Past date" : "No open windows."}</div>
                  ) : (
                    <div className="space-y-1">
                      <div>
                        {dayAvailability.status === "open"
                          ? "Open windows:"
                          : dayAvailability.status === "limited"
                          ? "Limited windows:"
                          : "Busy windows:"}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {dayAvailability.freeWindows.map((w, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/60 border border-current/20"
                          >
                            {`${String(Math.floor(w.start / 60)).padStart(2, "0")}:${String(w.start % 60).padStart(2, "0")} - ${String(Math.floor(w.end / 60)).padStart(2, "0")}:${String(w.end % 60).padStart(2, "0")}`}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {selectedDayEvents.length > 0 ? (
              <>
                {selectedDayEvents.length > 1 && (
                  <div className="flex items-center justify-between text-xs text-neutral-500">
                    <span>{selectedEventIndex + 1} of {selectedDayEvents.length}</span>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setSelectedEventIndex((prev) =>
                            prev === 0 ? selectedDayEvents.length - 1 : prev - 1
                          )
                        }
                      >
                        ←
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setSelectedEventIndex((prev) =>
                            prev === selectedDayEvents.length - 1 ? 0 : prev + 1
                          )
                        }
                      >
                        →
                      </Button>
                    </div>
                  </div>
                )}

                {(() => {
                  const event = selectedDayEvents[selectedEventIndex];
                  const isPrivate = !!event.blocked;
                  const title = isPrivate ? (event.title || "Private booking") : event.title;
                  const description = isPrivate
                    ? event.description || "This date is reserved for a private booking."
                    : event.description;
                  const location = isPrivate ? event.location || "Location withheld" : event.location;
                  return (
                    <div className="space-y-2">
                      <h5 className="text-lg font-semibold" style={{ color: "#0f172a" }}>{title}</h5>
                      {!isPrivate && event.time && (
                        <div className="text-sm" style={{ color: "#1f2937" }}>
                          <span className="inline-flex items-center gap-1">
                            <Icon name="clock" className="h-4 w-4" />
                            {event.time}
                          </span>
                        </div>
                      )}
                      {description && (
                        <p className="text-sm" style={{ color: "#1f2937" }}>
                          {description}
                        </p>
                      )}
                      {location && (
                        <p className="text-sm" style={{ color: "#1f2937" }}>
                          <span className="inline-flex items-center gap-1">
                            <Icon name="map-pin" className="h-4 w-4" />
                            {location}
                          </span>
                        </p>
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        {event.category && !isPrivate && (
                          <span className="inline-block px-2 py-1 text-xs rounded-full bg-secondary/20 text-secondary">
                            {event.category}
                          </span>
                        )}
                        {event.href && !isPrivate && (
                          <Button asChild variant="outline" size="sm" className="text-primary border-primary/30">
                            <a href={event.href} target="_blank" rel="noopener noreferrer" className="text-primary">
                              {event.linkText || "Learn More"} →
                            </a>
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </>
            ) : (
              <div className="text-sm text-neutral-600">No events scheduled for this date.</div>
            )}
            {bookingHref && selectedDate && availabilityForDay(selectedDate).status !== "past" && (
              <div className="pt-3 border-t border-neutral-200 flex justify-end">
                <Button asChild variant="primary" size="sm">
                  <a href={buildBookingHref(selectedDate) ?? bookingHref}>{bookingLabel}</a>
                </Button>
              </div>
            )}
          </div>
        </Modal>
      </>
    );
  };

  const renderListVariant = () => (
    <div className="space-y-6">
      {events.length === 0 ? (
        <Card className="p-6 bg-white/80 text-[var(--color-text,#0f172a)] border border-[color:var(--color-text,#0f172a)]/10 shadow-sm rounded-2xl">
          <CardContent className="p-0 space-y-3">
            <div className="text-base font-semibold">No upcoming events listed yet</div>
            <p className="text-sm" style={{ color: "var(--color-text,#0f172a)", opacity: 0.72 }}>
              Follow our Facebook for daily route updates or request a booking to reserve your date.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="primary" size="sm">
                <a href="/book">Book an event</a>
              </Button>
              {calendarLink && (
                <Button asChild variant="outline" size="sm">
                  <a href={calendarLink.href}>{calendarLink.text}</a>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        events.map((event, index) => (
          <Card
            key={index}
            className="p-6 shadow-sm-2 hover:shadow-md-2 transition border border-[color:var(--color-text,#0f172a)]/10 bg-[var(--color-surface,#fff)] text-[var(--color-text,#0f172a)]"
          >
            <CardContent className="p-0">
              <div className="flex flex-col md:flex-row md:items-start gap-4">
                <div className="md:w-32 flex-shrink-0">
                  <div className="text-sm font-semibold text-primary">
                    {formatDate(event.date)}
                  </div>
                  {event.time && (
                    <div className="text-sm" style={{ color: "var(--color-text,#0f172a)", opacity: 0.78 }}>
                      {event.time}
                    </div>
                  )}
                </div>
                <div className="flex-grow">
                  <h3 className="text-xl font-semibold mb-2">{event.title}</h3>
                  {event.description && (
                    <p className="text-sm mb-2" style={{ color: "var(--color-text,#0f172a)", opacity: 0.8 }}>
                      {event.description}
                    </p>
                  )}
                  {event.location && (
                    <p className="text-sm mb-3" style={{ color: "var(--color-text,#0f172a)", opacity: 0.75 }}>
                      <span className="inline-flex items-center gap-1">
                        <Icon name="map-pin" className="h-4 w-4" />
                        {event.location}
                      </span>
                    </p>
                  )}
                  {event.href && (
                    <Button asChild variant="outline" size="sm">
                      <a href={event.href}>
                        {event.linkText || "Learn More"}
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );

  const renderGridVariant = () => (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {events.map((event, index) => (
        <Card key={index} className="p-6 flex flex-col bg-white/90 shadow-sm-2 hover:shadow-md-2 transition">
          <CardContent className="p-0 flex-grow">
            <div className="text-sm font-semibold text-primary mb-2">
              {formatDate(event.date)}
            </div>
            {event.time && (
              <div className="text-sm text-neutral-600 mb-3">{event.time}</div>
            )}
            <h3 className="text-xl font-semibold mb-2">{event.title}</h3>
            {event.description && (
              <p className="text-neutral-600 mb-3">{event.description}</p>
            )}
            {event.location && (
              <p className="text-sm text-neutral-500 mb-3">
                <span className="inline-flex items-center gap-1">
                  <Icon name="map-pin" className="h-4 w-4" />
                  {event.location}
                </span>
              </p>
            )}
            {event.href && (
              <div className="mt-auto pt-4">
                <Button asChild variant="outline" size="sm" className="w-full">
                  <a href={event.href}>
                    {event.linkText || "Learn More"}
                  </a>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const renderTimelineVariant = () => (
    <div className="space-y-8 relative before:absolute before:left-[15px] before:top-0 before:bottom-0 before:w-px before:bg-neutral-200 md:before:left-[100px]">
      {events.map((event, index) => (
        <div key={index} className="relative pl-10 md:pl-32">
          <div className="absolute left-0 top-0 md:w-24 text-sm font-semibold text-primary">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-xs">
                {parseLocalDate(event.date).getDate()}
              </div>
              <div className="hidden md:block">
                {formatDate(event.date).replace(/\d+,/, "")}
              </div>
            </div>
          </div>
          <Card className="p-6 bg-white/90 shadow-sm-2 hover:shadow-md-2 transition">
            <CardContent className="p-0">
              <div className="md:hidden text-sm font-semibold text-primary mb-2">
                {formatDate(event.date)}
              </div>
              {event.time && (
                <div className="text-sm text-neutral-600 mb-2">{event.time}</div>
              )}
              <h3 className="text-xl font-semibold mb-2">{event.title}</h3>
              {event.description && (
                <p className="text-neutral-600 mb-2">{event.description}</p>
              )}
            {event.location && (
              <p className="text-sm text-neutral-500 mb-3">
                <span className="inline-flex items-center gap-1">
                  <Icon name="map-pin" className="h-4 w-4" />
                  {event.location}
                </span>
              </p>
            )}
              {event.href && (
                <Button asChild variant="outline" size="sm">
                  <a href={event.href}>
                    {event.linkText || "Learn More"}
                  </a>
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      ))}
    </div>
  );

  return (
    <BandedSection variant={bandVariant} className="py-16 md:py-24" contentClassName={className}>
      {(title || subtitle) && (
        <div className="text-center mb-12">
          {title && <SectionTitle>{title}</SectionTitle>}
          {subtitle && <SectionSubtitle className="mt-4">{subtitle}</SectionSubtitle>}
        </div>
      )}

      {variant === "list" && renderListVariant()}
      {variant === "grid" && renderGridVariant()}
      {variant === "timeline" && renderTimelineVariant()}
      {variant === "calendar" && renderCalendarVariant()}

      {calendarLink && (
        <div className="text-center mt-12">
          <Button asChild variant="primary" size="lg">
            <a href={calendarLink.href}>{calendarLink.text}</a>
          </Button>
        </div>
      )}
    </BandedSection>
  );
}
